import React, {
  useEffect, useState, useRef, useContext,
} from 'react';
import { NavLink } from 'react-router-dom';
import Carousel from 'react-multi-carousel';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';

import { AppDataContext } from '../../context';
import { useCourseCatalogue, useDebounce } from '../../hooks';
import { getNextCourseArrays, getEnabledCourse } from '../../utils';
import type { CourseCatalogueItem } from '../../types';
import SpinLoader from '../SpinLoader';

import 'react-multi-carousel/lib/styles.css';
import './index.scss';
import messages from './messages';

import arrowRightImg from '../../img/arrow-right.svg';
import carouselArrowRightImg from '../../img/carousel-arrow-right.svg';
import carouselArrowLeftImg from '../../img/carousel-arrow-left.svg';

interface CarouselCourseCardProps {
  courseID?: string;
}

interface CustomArrowProps {
  direction?: 'right' | 'left';
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
}

const CustomArrow: React.FC<CustomArrowProps> = ({ direction = 'right', onClick = () => {} }) => (
  <button className={`custom-arrow-btn __${direction}`} type="button" onClick={onClick}>
    <img
      src={direction === 'right' ? carouselArrowRightImg : carouselArrowLeftImg}
      alt={`Carousel arrow ${direction}`}
    />
  </button>
);

const CarouselCourseCard: React.FC<CarouselCourseCardProps> = ({ courseID = '' }) => {
  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 2,
      slidesToSlide: 2, // optional, default to 1.
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
      slidesToSlide: 2, // optional, default to 1.
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
      slidesToSlide: 1, // optional, default to 1.
    },
  };
  const [filteredCourses, setFilteredCourses] = useState<CourseCatalogueItem[]>([]);
  const carouselRef = useRef<any>(null);
  const [coursesLoaded, setCoursesLoaded] = useState<boolean>(false);

  const intl = useIntl();
  const { allowedCourses } = useContext(AppDataContext);
  const [coursesData, courseDataLoaded] = useCourseCatalogue(allowedCourses);

  useEffect(() => {
    if (courseDataLoaded && coursesData) {
      const enabledCourses = getEnabledCourse(coursesData, allowedCourses);
      if (courseID) {
        const promise: Promise<CourseCatalogueItem[]> = new Promise((resolve) => {
          if (courseDataLoaded) {
            const isEnabled = getEnabledCourse(coursesData, allowedCourses);
            if (courseID) {
              const nextCourseData = getNextCourseArrays(courseID, isEnabled, allowedCourses);
              // @ts-ignore
              resolve(nextCourseData);
            } else {
              resolve(isEnabled);
            }
          }
        });
        promise.then((updatedFilteredCourse) => {
          setFilteredCourses(updatedFilteredCourse);
        });
      } else {
        setFilteredCourses(enabledCourses);
      }
    }
  }, [courseDataLoaded, coursesData]);

  useDebounce(() => {
    if (filteredCourses.length > 0) {
      setCoursesLoaded(true);
    }
  }, 300, [filteredCourses]);

  if (!coursesLoaded) {
    return (
      <SpinLoader size={30} containerHeight="200px" color="var(--cobalt)" />
    );
  }

  return (
    <Carousel
      ssr={false}
      partialVisible
      responsive={responsive}
      containerClass="course-container"
      ref={carouselRef}
      customLeftArrow={<CustomArrow direction="left" />}
      customRightArrow={<CustomArrow direction="right" />}
    >
      {filteredCourses.map((courseInstance, index) => (
        <div
          className="course-item-custom carousel-course-item"
          key={`carousel-item-${courseInstance?.search?.data?.id}`}
        >
          <div className="top-c">
            <img
              src={`${getConfig().LMS_BASE_URL}${courseInstance?.search?.data?.image_url}`}
              alt={courseInstance?.search?.data?.content?.display_name || intl.formatMessage(messages.courseImageAlt)}
            />
          </div>
          <div className="c-name">
            {courseInstance?.search?.data?.content?.display_name}
          </div>
          <p className="fixed-height course-item-desc">
            {courseInstance?.metadata
              ? courseInstance?.metadata?.description || intl.formatMessage(messages.noDescription)
              : ''}
          </p>
          <div className="home-btn">
            <NavLink
              to={`/course-overview/${courseInstance?.search?.data?.id}`}
              className={`course-btn ${index === 0 ? 'gs' : 'lm'}`}
            >
              <span className="btn-d-flex">
                {intl.formatMessage(index === 0 ? messages.getStarted : messages.learnMore)}
                {index === 0 && (
                  <img className="carousel-btn-icon" src={arrowRightImg} alt="" />
                )}
              </span>
            </NavLink>
          </div>
        </div>
      ))}
    </Carousel>
  );
};

export default CarouselCourseCard;
